import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-decimal-input',
  standalone: true,
  template: `
    <input
      type="text"
      inputmode="decimal"
      [value]="displayValue"
      [attr.placeholder]="placeholder"
      [disabled]="disabled"
      [style]="inputStyle"
      (focus)="handleFocus()"
      (blur)="handleBlur()"
      (input)="handleInput($event)"
      [readonly]="readonly"
    />
  `,
  styles: `
    input:read-only {
      border: none;
      outline: none;
      background: black;
      color: white;
      margin-left: 2px;
      margin-right: 2px;
    }
  `,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DecimalInputComponent),
    multi: true
  }]
})
export class DecimalInputComponent implements ControlValueAccessor {
  @Input() inputStyle = '';
  @Input() readonly = false;
  @Input() positiveOnly = false;

  /** モデル（decimal小数文字列）の小数桁 */
  @Input() scale = 2;
  @Input() displayScale: number|undefined = undefined;

  /** 3桁区切り（blur時のみ） */
  @Input() useThousandsSeparator = true;

  /** %として入力・表示するか（モデルは小数のまま） */
  @Input() asPercent = false;

  /** %表示時の小数桁（例: 2 → 12.34%） */
  @Input() percentScale = 2;

  @Input() placeholder = '';

  disabled = false;

  /** モデル値（DECIMAL文字列、カンマ無し、小数=0.1234） */
  private modelValue: string | null = null;

  /** inputに表示する値（focus: 生値、blur: 整形済） */
  displayValue = '';

  private onChange: (v: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: unknown): void {
    const v = (value === null || value === undefined || value === '') ? null : String(value);
    this.modelValue = v;
    this.displayValue = this.formatForDisplay(v);
  }

  registerOnChange(fn: (v: string | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  handleFocus() {
    let value = this.modelValue;

    // 念のためモデルが負なら正に補正
    if (this.positiveOnly && value?.startsWith('-')) {
      value = value.slice(1);
      this.modelValue = value;
      this.onChange(value);
    }

    if (this.asPercent) {
      const pct = value ? this.shiftDecimal(value, +2) : null;
      this.displayValue = pct ?? '';
    } else {
      this.displayValue = value ?? this.stripCommas(this.displayValue);
    }
  }

  handleInput(ev: Event) {
    const el = ev.target as HTMLInputElement;
    const raw = el.value;

    const sanitized = this.sanitizeTyping(raw);

    // ★ここが肝：表示もDOMも必ずサニタイズ後に揃える
    if (sanitized !== raw) {
      el.value = sanitized;
    }
    this.displayValue = sanitized;

    // 未確定入力は null 扱い（positiveOnly=true なら '-' は sanitized='' になるのでここに入る）
    if (sanitized === '' || sanitized === '.' || sanitized === '-' || sanitized === '-.') {
      this.modelValue = null;
      this.onChange(null);
      return;
    }

    const typing = this.stripCommas(sanitized);
    let model = this.asPercent ? this.shiftDecimal(typing, -2) : typing;

    if (this.positiveOnly && model.startsWith('-')) {
      model = model.slice(1);
    }

    this.modelValue = model;
    this.onChange(model);
  }

  handleBlur() {
    this.onTouched();

    // blur時に「確定丸め」してモデル・表示を整える
    if (this.asPercent) {
      // いまの表示（%）を正規化 → 小数へ戻す → モデルscaleで正規化
      const pctNormalized = this.normalizeToScale(this.stripCommas(this.displayValue), this.percentScale);
      const decimal = pctNormalized ? this.shiftDecimal(pctNormalized, -2) : null;
      const decimalNormalized = this.normalizeToScale(decimal, this.scale);

      this.modelValue = decimalNormalized;
      this.onChange(decimalNormalized);
      this.displayValue = this.formatForDisplay(decimalNormalized);
      return;
    }

    const normalized = this.normalizeToScale(this.modelValue, this.scale);
    this.modelValue = normalized;
    this.onChange(normalized);
    this.displayValue = this.formatForDisplay(normalized);
  }

  // ---------- formatting / parsing ----------

  private formatForDisplay(modelDecimal: string | null): string {
    if (!modelDecimal) return '';

    if (this.asPercent) {
      // 小数 → %（*100）し、%用scaleで丸めて表示
      const pct = this.shiftDecimal(modelDecimal, +2);
      const pctNorm = this.normalizeToScale(pct, this.percentScale);
      return this.formatPlainDecimal(pctNorm);
    }

    // 通常（小数）の表示
//    const norm = this.normalizeToScale(modelDecimal, this.scale);
    const norm = this.normalizeToScale(modelDecimal, this.displayScale ? this.displayScale : this.scale);
    return this.formatPlainDecimal(norm);
  }

  private formatPlainDecimal(value: string | null): string {
    if (!value) return '';
    const { sign, intPart, fracPart } = this.splitDecimal(value);

    const intFormatted = this.useThousandsSeparator
      ? this.addThousandsSeparator(intPart)
      : intPart;

    return fracPart.length > 0
      ? `${sign}${intFormatted}.${fracPart}`
      : `${sign}${intFormatted}`;
  }

  /** 10^places ぶん小数点を移動（places=+2 なら *100、-2 なら /100） */
  private shiftDecimal(value: string, places: number): string {
    let v = value.trim();

    if (this.positiveOnly && v.startsWith('-')) {
      v = v.slice(1);
    }

    const sign = v.startsWith('-') ? '-' : '';
    const body = sign ? v.slice(1) : v;

    const [i = '0', f = ''] = body.split('.');
    const digits = (i + f).replace(/^0+(?=\d)/, '') || '0';
    const fracLen = f.length;

    // 現在の小数点位置（digitsの末尾から fracLen）
    const pointFromRight = fracLen;
    // 新しい小数点位置（末尾から）
    const newPointFromRight = pointFromRight - places;

    let outInt = '';
    let outFrac = '';

    if (newPointFromRight <= 0) {
      // 小数点が右へ（=掛け算）
      outInt = digits + '0'.repeat(-newPointFromRight);
      outFrac = '';
    } else if (newPointFromRight >= digits.length) {
      // 小数点が左へ（=割り算）
      outInt = '0';
      outFrac = '0'.repeat(newPointFromRight - digits.length) + digits;
    } else {
      const cut = digits.length - newPointFromRight;
      outInt = digits.slice(0, cut) || '0';
      outFrac = digits.slice(cut);
    }

    // 整形（末尾ゼロは消さない：丸めは normalizeToScale が担当）
    return sign + (outFrac ? `${outInt}.${outFrac}` : outInt);
  }

  private sanitizeTyping(s: string): string {
    // 入力中はカンマを許可しない（blur整形で付ける）
    let v = s.replace(/[^0-9.\-]/g, '');

    if (this.positiveOnly) {
      v = v.replace(/-/g, '');
    }

    const minus = !this.positiveOnly && v.startsWith('-') ? '-' : '';
    v = v.replace(/-/g, '');

    const parts = v.split('.');
    if (parts.length > 1) v = parts[0] + '.' + parts.slice(1).join('');

    return minus + v;
  }

  private stripCommas(s: unknown): string {
    return String(s ?? '').replace(/,/g, '');
  }

  private splitDecimal(value: string): { sign: string; intPart: string; fracPart: string } {
    const v = value.trim();
    const sign = v.startsWith('-') ? '-' : '';
    const body = sign ? v.slice(1) : v;
    const [iRaw, fRaw = ''] = body.split('.');
    const intPart = (iRaw ?? '').replace(/^0+(?=\d)/, '') || '0';
    const fracPart = fRaw ?? '';
    return { sign, intPart, fracPart };
  }

  private addThousandsSeparator(intPart: string): string {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /** 四捨五入 half-up で scale 桁に正規化（BigIntベース） */
  private normalizeToScale(value: string | null, scale: number): string | null {
    if (value == null) return null;

    let v = String(value);

    if (this.positiveOnly && v.startsWith('-')) {
      v = v.slice(1);
    }

    const stripped = this.stripCommas(v).trim();
    if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') return null;

    const { sign: origSign, intPart, fracPart } = this.splitDecimal(stripped);
    const sign = this.positiveOnly ? '' : origSign;

    const s = Math.max(0, Math.floor(scale));

    const fracPadded = (fracPart + '0'.repeat(s + 1)).slice(0, s + 1);
    const keep = fracPadded.slice(0, s);
    const roundDigit = fracPadded.slice(s, s + 1);

    const combined = BigInt((intPart || '0') + (keep || ''));
    const shouldRoundUp = roundDigit !== '' && roundDigit >= '5';
    const rounded = shouldRoundUp ? combined + 1n : combined;

    let roundedStr = rounded.toString();

    if (s === 0) return sign ? '-' + roundedStr : roundedStr;

    if (roundedStr.length <= s) {
      roundedStr = '0'.repeat(s + 1 - roundedStr.length) + roundedStr;
    }

    const intOut = roundedStr.slice(0, -s) || '0';
    const fracOut = roundedStr.slice(-s);

    return sign ? `-${intOut}.${fracOut}` : `${intOut}.${fracOut}`;
  }
}
